/**
 * @file: LeadBundleSheet.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Bottom panel for "转为商机": the acted-on plan detail pinned at top, then the
 * other uncovered same-vehicle items due around the same time. Staff can widen
 * or narrow the match window (days before / after the target's next service
 * date) and pick which to bundle, then confirm to create one lead spanning the
 * target + selection. Candidates are fetched here so the window can re-query.
 */

import { Typography } from '@euler/app/components/typography';
import { BottomPanel } from '@euler/components/BottomPanel';
import { Icon } from '@euler/components/Icomoon';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LeadBundleCandidates, LeadBundleItem } from '@euler/model/lead/types';
import { useServiceFactory } from '@euler/services/factory';
import { dateFromValue } from '@euler/utils/datetime';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import subDays from 'date-fns/subDays';
import { Minus, Plus, X } from 'lucide-react-native';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

const kDefaultWindowDays = 30;
const kWindowStepDays = 15;
const kMaxWindowDays = 180;

const formatDate = (value: string | Date | undefined | null): string => {
  if (!value) return '—';
  try {
    return format(dateFromValue(value), 'yyyy-MM-dd');
  } catch {
    return '—';
  }
};

const clampWindow = (days: number): number =>
  Math.max(0, Math.min(days, kMaxWindowDays));

export const LeadBundleSheet = memo(
  ({
    show,
    planDetailId,
    leadId,
    anchorDate,
    submitting,
    onClose,
    onConfirm,
    // Defaults match the "转为商机" creation flow (anchored on a plan detail).
    // Pass `leadId` + `pinTarget={false}` to reuse the picker for adding items
    // to an existing lead: the candidate set is the lead's vehicle's other
    // uncovered details, with no single target to pin. `anchorDate` (the lead's
    // target service date) labels the window range when there's no pinned target.
    title = '转为商机',
    submittingLabel = '创建中…',
    pinTarget = true,
  }: {
    show?: boolean;
    planDetailId?: number;
    leadId?: string;
    anchorDate?: string;
    submitting?: boolean;
    onClose?: () => void;
    onConfirm: (planDetailIds: number[]) => void;
    title?: string;
    submittingLabel?: string;
    pinTarget?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const { leadService } = useServiceFactory();

    const [before, setBefore] = useState(kDefaultWindowDays);
    const [after, setAfter] = useState(kDefaultWindowDays);
    const [data, setData] = useState<LeadBundleCandidates | undefined>();
    const [selected, setSelected] = useState<Set<number>>(new Set());

    // A different anchor opening the panel resets the window + selection.
    useEffect(() => {
      setBefore(kDefaultWindowDays);
      setAfter(kDefaultWindowDays);
      setSelected(new Set());
    }, [planDetailId, leadId]);

    // (Re)fetch candidates when opened or when the window changes. Debounced so
    // rapid +/- taps coalesce into one request; the previous candidates stay
    // rendered until the new set arrives (no loading flash on re-query).
    useEffect(() => {
      if (!show) return;
      let active = true;
      const timer = setTimeout(() => {
        void (async () => {
          try {
            const res =
              leadId != null
                ? await leadService.getBundleCandidatesForLead(
                    leadId,
                    before,
                    after,
                  )
                : planDetailId != null
                ? await leadService.getBundleCandidates(
                    planDetailId,
                    before,
                    after,
                  )
                : null;
            if (res && active) setData(res);
          } catch (e) {
            console.error(e);
          }
        })();
      }, 200);
      return () => {
        active = false;
        clearTimeout(timer);
      };
    }, [show, planDetailId, leadId, before, after, leadService]);

    const candidates = useMemo(() => data?.candidates ?? [], [data]);

    // Drop any selection no longer in range after the window narrowed.
    useEffect(() => {
      const ids = new Set(candidates.map(c => c.planDetailId));
      setSelected(prev => {
        const next = new Set([...prev].filter(id => ids.has(id)));
        return next.size === prev.size ? prev : next;
      });
    }, [candidates]);

    const allSelected =
      candidates.length > 0 && selected.size === candidates.length;

    const toggle = useCallback((id: number) => {
      setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);

    const toggleAll = useCallback(() => {
      setSelected(prev =>
        prev.size === candidates.length
          ? new Set()
          : new Set(candidates.map(c => c.planDetailId)),
      );
    }, [candidates]);

    const onConfirmPress = useCallback(() => {
      if (!data || submitting) return;
      const ids =
        pinTarget && data.target
          ? [data.target.planDetailId, ...selected]
          : [...selected];
      if (!ids.length) return;
      onConfirm(ids);
    }, [data, onConfirm, pinTarget, selected, submitting]);

    const total = (pinTarget ? 1 : 0) + selected.size;

    const anchor = data?.target?.nextServiceDate ?? anchorDate;
    const rangeFrom = anchor
      ? format(subDays(parseISO(anchor), before), 'yyyy-MM-dd')
      : '—';
    const rangeTo = anchor
      ? format(addDays(parseISO(anchor), after), 'yyyy-MM-dd')
      : '—';

    return (
      <BottomPanel visible={!!show} onClose={onClose ?? (() => undefined)}>
        <View style={{ flex: 1 }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              height: rpx(56),
              paddingHorizontal: rpx(16),
              borderBottomWidth: StyleSheet.hairlineWidth,
              borderBottomColor: '#ececec',
              gap: rpx(8),
            }}
          >
            <View style={{ width: rpx(24) }} />
            <Typography.Text
              size={rpx(16)}
              color="#111"
              style={{
                flex: 1,
                textAlign: 'center',
                fontFamily: FontFamily.NotoSans.Bold,
              }}
            >
              {title}
            </Typography.Text>
            <TouchableOpacity
              onPress={onClose ?? (() => undefined)}
              hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
              style={{ paddingHorizontal: rpx(4) }}
            >
              <X size={rpx(20)} color="#6b7280" />
            </TouchableOpacity>
          </View>

          {data ? (
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ padding: rpx(16) }}
            >
              {pinTarget && data.target ? (
                <>
                  <Text
                    style={{
                      fontSize: rpx(12),
                      color: '#94a3b8',
                      marginBottom: rpx(6),
                    }}
                  >
                    当前保养项
                  </Text>
                  <ItemRow item={data.target} pinned />
                </>
              ) : null}

              <WindowControl
                before={before}
                after={after}
                rangeFrom={rangeFrom}
                rangeTo={rangeTo}
                onBefore={d => setBefore(c => clampWindow(c + d))}
                onAfter={d => setAfter(c => clampWindow(c + d))}
              />

              {candidates.length ? (
                <>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: rpx(14),
                      marginBottom: rpx(8),
                    }}
                  >
                    <Text
                      style={{
                        fontSize: rpx(13),
                        color: '#334155',
                        fontWeight: '600',
                        flex: 1,
                      }}
                    >
                      合并相关保养项 (可选)
                    </Text>
                    <TouchableOpacity activeOpacity={0.7} onPress={toggleAll}>
                      <Text
                        style={{
                          fontSize: rpx(13),
                          color: '#2669db',
                          fontWeight: '600',
                        }}
                      >
                        {allSelected ? '取消全选' : '全选'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  {candidates.map(c => (
                    <ItemRow
                      key={c.planDetailId}
                      item={c}
                      checked={selected.has(c.planDetailId)}
                      onToggle={() => toggle(c.planDetailId)}
                    />
                  ))}
                </>
              ) : (
                <Text
                  style={{
                    fontSize: rpx(13),
                    color: '#94a3b8',
                    marginTop: rpx(14),
                  }}
                >
                  当前时间范围内暂无其他可合并的保养项
                </Text>
              )}
            </ScrollView>
          ) : (
            <View
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ActivityIndicator color="#94a3b8" />
            </View>
          )}

          <TouchableOpacity
            activeOpacity={0.8}
            onPress={onConfirmPress}
            disabled={submitting || total === 0}
            style={{
              height: rpx(50),
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor:
                submitting || total === 0 ? '#a6c1ef' : '#2669db',
            }}
          >
            <Text
              style={{ color: '#fff', fontSize: rpx(16), fontWeight: '600' }}
            >
              {submitting ? submittingLabel : `确定 (${total}项)`}
            </Text>
          </TouchableOpacity>
        </View>
      </BottomPanel>
    );
  },
);

/** Adjustable match window: ± days around the target's next service date. */
const WindowControl = memo(
  ({
    before,
    after,
    rangeFrom,
    rangeTo,
    onBefore,
    onAfter,
  }: {
    before: number;
    after: number;
    rangeFrom: string;
    rangeTo: string;
    onBefore: (delta: number) => void;
    onAfter: (delta: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={{ marginTop: rpx(14), marginBottom: rpx(8) }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: rpx(8),
            gap: rpx(6),
          }}
        >
          <Text
            style={{ fontSize: rpx(13), color: '#334155', fontWeight: '600' }}
          >
            合并时间范围
          </Text>
          <Text style={{ fontSize: rpx(12), color: '#64748b' }}>
            {rangeFrom} ~ {rangeTo}
          </Text>
        </View>
        <View style={{ flexDirection: 'row', gap: rpx(10) }}>
          <Stepper
            label="向后"
            value={before}
            onChange={onBefore}
            step={kWindowStepDays}
          />
          <Stepper
            label="向前"
            value={after}
            onChange={onAfter}
            step={kWindowStepDays}
          />
        </View>
        <Text
          style={{ fontSize: rpx(11), color: '#94a3b8', marginTop: rpx(8) }}
        >
          (以当前项保养日期为基准)
        </Text>
      </View>
    );
  },
);

const Stepper = memo(
  ({
    label,
    value,
    step,
    onChange,
  }: {
    label: string;
    value: number;
    step: number;
    onChange: (delta: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderWidth: 1,
          borderColor: '#e5e7eb',
          borderRadius: rpx(8),
          paddingHorizontal: rpx(10),
          height: rpx(38),
        }}
      >
        <Text style={{ fontSize: rpx(12), color: '#64748b' }}>{label}</Text>
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}
        >
          <TouchableOpacity
            activeOpacity={0.6}
            onPress={() => onChange(-step)}
            hitSlop={{ top: 8, right: 6, bottom: 8, left: 6 }}
          >
            <Minus size={rpx(16)} color="#475569" />
          </TouchableOpacity>
          <Text
            style={{
              fontSize: rpx(13),
              color: '#0f172a',
              minWidth: rpx(42),
              textAlign: 'center',
            }}
          >
            {value} 天
          </Text>
          <TouchableOpacity
            activeOpacity={0.6}
            onPress={() => onChange(step)}
            hitSlop={{ top: 8, right: 6, bottom: 8, left: 6 }}
          >
            <Plus size={rpx(16)} color="#475569" />
          </TouchableOpacity>
        </View>
      </View>
    );
  },
);

const ItemRow = memo(
  ({
    item,
    pinned,
    checked,
    onToggle,
  }: {
    item: LeadBundleItem;
    pinned?: boolean;
    checked?: boolean;
    onToggle?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        activeOpacity={pinned ? 1 : 0.6}
        onPress={pinned ? undefined : onToggle}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: pinned ? '#f8fafc' : '#fff',
          borderWidth: 1,
          borderColor: checked ? '#2669db' : '#e5e7eb',
          borderRadius: rpx(8),
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(10),
          marginBottom: rpx(8),
        }}
      >
        {!pinned ? (
          <Icon
            name={checked ? 'checkbox' : 'checkbox_outline'}
            size={rpx(20)}
            color={checked ? '#2669db' : '#cbd5e1'}
            style={{ marginRight: rpx(10) }}
          />
        ) : null}
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: rpx(14), color: '#0f172a' }}>
            {item.customLabel || item.name}
          </Text>
          <Text style={{ fontSize: rpx(12), color: '#94a3b8' }}>
            建议 {formatDate(item.nextServiceDate)}
            {item.nextServiceMileage ? ` · ${item.nextServiceMileage} km` : ''}
          </Text>
        </View>
      </TouchableOpacity>
    );
  },
);
